import React, {useCallback, useEffect, useState} from 'react'
import {MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents} from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import {ProjectAddress, ProjectAddressMapPosition} from '../../entities/project'
import {useIntl} from 'react-intl'
import {Map} from 'leaflet'
import domtoimage from 'dom-to-image-more'
;(window as any).domtoimage = domtoimage

export type MapPosition = {}
export type MapImage = {srcUrl: string}
export type MapImageExtractor = () => Promise<MapImage>
export type MapMetersPerPixelExtractor = () => number

export type EditorProjectMapProps = {
  mapPosition: ProjectAddressMapPosition
  onMapPositionUpdated: (position: ProjectAddressMapPosition) => void
  onMapImageExtractorReady?: (extractor: MapImageExtractor) => void
  onMetersPerPixelExtractorReady?: (extractor: MapMetersPerPixelExtractor) => void
}

export type MapControllerProps = {
  onMapReady: (m: Map) => void
  onMapPositionUpdated: EditorProjectMapProps['onMapPositionUpdated']
}

export function MapInstanceProvider({onMapReady, onMapPositionUpdated}: MapControllerProps) {
  const map = useMap()

  const updateMapPosition = useCallback(() => {
    const {lat, lng} = map.getCenter()
    onMapPositionUpdated({
      latitude: lat,
      longtitude: lng,
      zoomLevel: map.getZoom(),
      mapMetersPerPixel: getMetersPerPixel(map),
    })
  }, [map, onMapPositionUpdated])

  useMapEvents({
    dragend: updateMapPosition,
    zoomend: updateMapPosition,
  })

  useEffect(() => {
    onMapReady(map)
  }, [map])

  return null
}

export function EditorProjectMap({
  mapPosition,
  onMapPositionUpdated,
  onMapImageExtractorReady,
  onMetersPerPixelExtractorReady,
}: EditorProjectMapProps) {
  const intl = useIntl()
  const [map, setMap] = useState<Map | null>(null)

  const {latitude, longtitude, zoomLevel} = mapPosition

  useEffect(() => {
    if (!mapPosition || !map) {
      return
    }

    map.setView([latitude, longtitude], zoomLevel || map.getZoom())
  }, [latitude, longtitude, zoomLevel])

  // TODO: move up
  // if (!mapPosition) {
  //   return <div>{intl.formatMessage({id: 'noInformationAboutProjectLocation'})}</div>
  // }

  return (
    <MapContainer
      style={{height: '500px'}}
      center={[latitude, longtitude]}
      zoom={mapPosition.zoomLevel}
      scrollWheelZoom={false}
      attributionControl={false}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <MapInstanceProvider
        onMapReady={(m) => {
          setMap(m)
          if (typeof onMapImageExtractorReady === 'function') {
            onMapImageExtractorReady(async () => {
              const container = m.getContainer()
              const {clientWidth: width, clientHeight: height} = container
              const pane = m.getPane('mapPane')
              const paneCloned = pane.cloneNode(true) as HTMLElement
              paneCloned.style.opacity = '0'
              document.body.appendChild(paneCloned)

              const imageDataUrl = await domtoimage.toJpeg(paneCloned, {
                width,
                height,
                quality: 0.95,
                style: {opacity: '1'},
              })

              document.body.removeChild(paneCloned)
              return {srcUrl: imageDataUrl}
            })
          }

          if (typeof onMetersPerPixelExtractorReady === 'function') {
            onMetersPerPixelExtractorReady(() => getMetersPerPixel(m))
          }
        }}
        onMapPositionUpdated={onMapPositionUpdated}
      />
    </MapContainer>
  )
}

export function getMetersPerPixel(map: Map) {
  const centerLatLng = map.getCenter() // get map center
  const pointC = map.latLngToContainerPoint(centerLatLng) // convert to containerpoint (pixels)
  const pointX = [pointC.x + 1, pointC.y] as [number, number] // add one pixel to x
  const pointY = [pointC.x, pointC.y + 1] as [number, number] // add one pixel to y

  // convert containerpoints to latlng's
  const latLngC = map.containerPointToLatLng(pointC)
  const latLngX = map.containerPointToLatLng(pointX)
  const latLngY = map.containerPointToLatLng(pointY)

  const distanceX = latLngC.distanceTo(latLngX) // calculate distance between c and x (latitude)
  const distanceY = latLngC.distanceTo(latLngY) // calculate distance between c and y (longitude)

  return (distanceX + distanceY) / 2
}
